import React, { useCallback } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { CASH_OFFER_ADDRESS_INPUT_MIN_HEIGHT_CLASS } from './CashOfferAddressInput.constants';
import type { HubSectionId } from './hubSectionOrdering';

const CASH_OFFER_BACKGROUND_IMAGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2026-06-1782247760214-08136_offer_card_with_gardient.png?service=imgdrop';

interface RecommendationCardProps {
  title: string;
  description: string;
  backgroundImageUrl?: string;
  children?: React.ReactNode;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  title,
  description,
  backgroundImageUrl,
  children,
}) => (
  // No `overflow-hidden` here: the address autocomplete dropdown renders inline
  // (absolutely positioned) and must be able to extend past the card's bottom
  // edge. The photo clips itself to the rounded corner via `rounded-l-xl`.
  <Col className="relative rounded-xl bg-od-bg-secondary shadow-[0_0_0_1px_rgba(121,118,117,0.16)]">
    {backgroundImageUrl && (
      // The photo has the fade-to-surface baked in, so no overlay gradient is
      // applied. Hidden on mobile, where the card stacks to a single column.
      <Image
        alt=""
        src={backgroundImageUrl}
        className="pointer-events-none absolute inset-y-0 left-0 z-0 hidden h-full w-[260px] rounded-xl object-cover object-bottom md:block"
      />
    )}
    <Col className="relative z-[1] gap-6 px-6 py-6 md:pl-[284px]">
      <Col className="gap-2">
        <Text as="h3" className="text-label-xl text-od-content-primary">
          {title}
        </Text>
        <Text as="p" className="text-body-sm text-od-content-tertiary">
          {description}
        </Text>
      </Col>
      {children && <Col className="relative z-[1]">{children}</Col>}
    </Col>
  </Col>
);

interface RecommendationCardButtonProps {
  analyticsName: string;
  href: string;
  label: string;
}

const RecommendationCardButton: React.FC<RecommendationCardButtonProps> = ({
  analyticsName,
  href,
  label,
}) => {
  const router = useRouter();
  const handlePress = useCallback(() => {
    router.push(href);
  }, [router, href]);

  return (
    <Button
      analyticsName={analyticsName}
      aria-label={label}
      onClick={handlePress}
      size="sm"
      variant="secondaryInverse"
    >
      {label}
    </Button>
  );
};

// Skeleton for the code-split address input: a shimmering white field that
// matches the input's footprint so the card doesn't reflow when the chunk loads.
// White (not the card's secondary surface) keeps it visible against the card.
const CashOfferAddressInputPlaceholder = () => (
  <Box
    aria-hidden
    className={`${CASH_OFFER_ADDRESS_INPUT_MIN_HEIGHT_CLASS} skeleton w-full rounded-lg bg-od-bg-primary shadow-[0_0_0_1px_rgba(121,118,117,0.16)]`}
  />
);

const CashOfferAddressInputFallback: React.FC<{ section: HubSectionId }> = () => (
  <RecommendationCardButton
    label="Find offers"
    href="/"
    analyticsName="hub-recommendation-cash-offer-fallback"
  />
);

const CashOfferAddressInput = dynamic(
  () =>
    import('./CashOfferAddressInput')
      .then((mod) => mod.CashOfferAddressInput)
      .catch(() => CashOfferAddressInputFallback),
  { ssr: false, loading: CashOfferAddressInputPlaceholder },
);

// The "Opendoor mortgages" card was removed from this section for now; restore
// it here alongside the cash-offer card when the mortgages flow is ready.
export const RecommendationsSection: React.FC = () => (
  <Col className="w-full">
    <RecommendationCard
      title="Get a cash offer on your home"
      description="See what your home is worth with a free, no-obligation cash offer from Opendoor."
      backgroundImageUrl={CASH_OFFER_BACKGROUND_IMAGE_URL}
    >
      <CashOfferAddressInput section="recommendations" />
    </RecommendationCard>
  </Col>
);
